import React, { useCallback, useEffect, useState } from 'react';
import { Table, Card, Collapse, message, Spin, Tag, Descriptions, Image, Row, Col } from 'antd';
import { ApartmentOutlined, HomeOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import { ProjectWebDTO, UnitWebDTO } from '../types/auth';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';

const { Panel } = Collapse;

const SFUnits: React.FC = () => {
  const [projects, setProjects] = useState<ProjectWebDTO[]>([]);
  const [unitsByProject, setUnitsByProject] = useState<Record<string, UnitWebDTO[]>>({});
  const [loading, setLoading] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState<string[]>([]);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    setLoading(true); 
    try {
      const projectsData = await apiService.getSFProjects();
      setProjects(projectsData);

      // Загружаем юниты для каждого проекта
      const unitsMap: Record<string, UnitWebDTO[]> = {};
      for (const project of projectsData) {
        try {
          const units = await apiService.getSFUnits(undefined, project.id);
          unitsMap[project.id] = units;
        } catch (error: any) {
          console.error(`Failed to load units for project ${project.id}:`, error);
          unitsMap[project.id] = [];
        }
      }
      setUnitsByProject(unitsMap);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view SF Units.');
      } else {
        message.error('Failed to load projects and units');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    
    loadData();
  }, [loadData, navigate]);

  const handlePanelChange = (keys: string | string[]) => {
    setExpandedProjects(Array.isArray(keys) ? keys : [keys]);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const unitColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'unitType',
      key: 'unitType',
      width: 120,
    },
    {
      title: 'Property Type',
      dataIndex: 'propertyType',
      key: 'propertyType',
      width: 120,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 150,
      render: (price: number) => formatPrice(price),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          'available': 'green',
          'sold': 'red',
          'reserved': 'orange',
          'pending': 'blue',
        };
        return <Tag color={colorMap[status.toLowerCase()] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Floor',
      dataIndex: 'floor',
      key: 'floor',
      width: 100,
    },
    {
      title: 'Total Area',
      dataIndex: 'totalArea',
      key: 'totalArea',
      width: 120,
      render: (area: number) => `${area}`,
    },
    {
      title: 'Bedrooms',
      dataIndex: 'bedrooms',
      key: 'bedrooms',
      width: 100,
    },
    {
      title: 'Bathrooms',
      dataIndex: 'bathrooms',
      key: 'bathrooms',
      width: 100,
    },
    {
      title: 'Handover Date',
      dataIndex: 'handoverDate',
      key: 'handoverDate',
      width: 150,
      render: (date: string) => formatDate(date),
    },
  ];

  const renderUnitDetails = (unit: UnitWebDTO) => {
    return (
      <Card size="small" style={{ marginTop: 8 }}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="ID">
            <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{unit.id}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Building">{unit.building || '-'}</Descriptions.Item>
          <Descriptions.Item label="Suit Area">{unit.suitArea}</Descriptions.Item>
          <Descriptions.Item label="Balcony Area">{unit.balconyArea}</Descriptions.Item>
          <Descriptions.Item label="Floors" span={2}>
            {unit.floors.length > 0 ? unit.floors.join(', ') : '-'}
          </Descriptions.Item>
        </Descriptions>
        
        {unit.images && (
          <div style={{ marginTop: 16 }}>
            <h4>Images</h4>
            <Row gutter={[8, 8]}>
              {unit.images.floorPlans && unit.images.floorPlans.length > 0 && (
                <Col span={24}>
                  <div>
                    <strong>Floor Plans:</strong>
                    <Image.PreviewGroup>
                      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                        {unit.images.floorPlans.map((url, idx) => (
                          <Col key={idx}>
                            <Image
                              width={100}
                              height={100}
                              src={url}
                              style={{ objectFit: 'cover' }}
                            />
                          </Col>
                        ))}
                      </Row>
                    </Image.PreviewGroup>
                  </div>
                </Col>
              )}
              {unit.images.layoutPreviews && unit.images.layoutPreviews.length > 0 && (
                <Col span={24}>
                  <div>
                    <strong>Layout Previews:</strong>
                    <Image.PreviewGroup>
                      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                        {unit.images.layoutPreviews.map((url, idx) => (
                          <Col key={idx}>
                            <Image
                              width={100}
                              height={100}
                              src={url}
                              style={{ objectFit: 'cover' }}
                            />
                          </Col>
                        ))}
                      </Row>
                    </Image.PreviewGroup>
                  </div>
                </Col>
              )}
            </Row>
          </div>
        )}

        {unit.payments && unit.payments.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <h4>Payment Plan</h4>
            <Table
              dataSource={unit.payments}
              columns={[
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'Milestone', dataIndex: 'milestoneName', key: 'milestoneName' },
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { 
                  title: 'Percentage', 
                  dataIndex: 'percentage', 
                  key: 'percentage',
                  render: (val: number) => `${val}%`
                },
                { 
                  title: 'Amount', 
                  dataIndex: 'amount', 
                  key: 'amount',
                  render: (val: number) => formatPrice(val)
                },
              ]}
              pagination={false}
              size="small"
              rowKey={(record, index) => `${record.name}-${index}`}
            />
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2>
          <ApartmentOutlined /> SF Units
        </h2>
      </div>

      {projects.length === 0 ? (
        <Card>
          <p>No projects found</p>
        </Card>
      ) : (
        <Collapse
          activeKey={expandedProjects}
          onChange={handlePanelChange}
          expandIconPosition="end"
        >
          {projects.map((project) => {
            const units = unitsByProject[project.id] || [];
            return (
              <Panel
                key={project.id}
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ApartmentOutlined />
                    <span style={{ fontWeight: 'bold' }}>{project.name}</span>
                    <Tag color="blue">{units.length} units</Tag>
                  </div>
                }
              >
                {units.length === 0 ? (
                  <p>No units found for this project</p>
                ) : (
                  <Table
                    dataSource={units}
                    columns={unitColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    expandable={{
                      expandedRowRender: (record) => renderUnitDetails(record),
                      expandIcon: ({ expanded, onExpand, record }) => (
                        <HomeOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ cursor: 'pointer' }}
                        />
                      ),
                    }}
                  />
                )}
              </Panel>
            );
          })}
        </Collapse>
      )}
    </div>
  );
};

export default SFUnits;

